import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Sprout, Search, Users, Package2, X, Eye, IndianRupee } from 'lucide-react';
import { orderService, plantService, getOrderItems } from '../../services/firebase';
import { Plant, Sale, SaleItem } from '../../types';

interface EnrichedOrder extends Sale {
  plant?: Plant | null;
  // For multi-plant orders, store enriched items with plant details
  enrichedItems?: (SaleItem & { plant?: Plant })[];
  // Maximum growth duration among all items (for delivery calculation)
  maxGrowthDuration?: number;
}

const SeedingPlanning: React.FC = () => {
  const [orders, setOrders] = useState<EnrichedOrder[]>([]);
  const [plantsById, setPlantsById] = useState<Record<string, Plant>>({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<EnrichedOrder | null>(null);
  const [seedingDateStr, setSeedingDateStr] = useState('');
  const [viewMode, setViewMode] = useState<'orders' | 'plants'>('orders');
  const [groupedOrders, setGroupedOrders] = useState<{ [plantId: string]: { plant: any; orders: Sale[]; totalTrays: number } }>({});
  const [selectedPlantGroup, setSelectedPlantGroup] = useState<string | null>(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [orderForDetails, setOrderForDetails] = useState<EnrichedOrder | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [pendingOrders, allPlants, grouped] = await Promise.all([
          orderService.getSeedingPendingOrders(),
          plantService.getAllPlants(),
          orderService.getOrdersGroupedByPlant()
        ]);
        const map: Record<string, Plant> = {};
        allPlants.forEach(p => { map[p.id] = p; });
        setPlantsById(map);

        // Enrich orders with plant details for both legacy and multi-plant orders
        const enriched = pendingOrders.map(o => {
          const items = getOrderItems(o);
          const enrichedItems = items.map(item => ({
            ...item,
            plant: item.plantId ? map[item.plantId] : undefined
          }));

          // Calculate max growth duration for delivery date calculation
          const maxGrowthDuration = Math.max(
            ...enrichedItems.map(item => item.plant?.growthDuration || 0),
            0
          );

          // For legacy orders, also set the plant property
          const plant = o.plantId ? map[o.plantId] : undefined;

          return { ...o, plant, enrichedItems, maxGrowthDuration };
        });

        setOrders(enriched);
        setGroupedOrders(grouped);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return orders.filter(o => {
      // Search by customer name or order ID
      if ((o.customerName || '').toLowerCase().includes(term)) return true;
      if ((o.id || '').toLowerCase().includes(term)) return true;
      if ((o.orderId || '').toLowerCase().includes(term)) return true;

      // Search through all plant items (handles both legacy and multi-plant orders)
      const items = o.enrichedItems || [];
      return items.some(item =>
        (item.plantName || '').toLowerCase().includes(term) ||
        (item.plantBrand || '').toLowerCase().includes(term)
      );
    });
  }, [orders, searchTerm]);

  const openSetDate = (order: EnrichedOrder) => {
    setSelectedOrder(order);
    setSeedingDateStr(new Date().toISOString().split('T')[0]);
    setShowModal(true);
  };

  const handleSaveSeeding = async () => {
    if (!selectedOrder || !seedingDateStr) return;

    // Use maxGrowthDuration for multi-plant orders, or single plant duration for legacy
    const growthDays = selectedOrder.maxGrowthDuration || selectedOrder.plant?.growthDuration || 0;

    // Debug logging to check values
    console.log('Seeding Assignment Debug:', {
      orderId: selectedOrder.id,
      isMultiPlant: (selectedOrder.enrichedItems?.length || 0) > 1,
      itemsCount: selectedOrder.enrichedItems?.length,
      maxGrowthDuration: selectedOrder.maxGrowthDuration,
      seedingDate: seedingDateStr,
      calculatedGrowthDays: growthDays
    });

    if (growthDays === 0) {
      alert('Warning: Plant growth duration is not set. Delivery date will be same as seeding date.');
    }

    await orderService.setSeedingDate(selectedOrder.id, new Date(seedingDateStr), growthDays);
    await refreshData();
    setShowModal(false);
    setSelectedOrder(null);
    setSeedingDateStr('');
  };

  const handleBulkSeeding = async () => {
    if (!selectedPlantGroup || !seedingDateStr) return;
    const plantGroup = groupedOrders[selectedPlantGroup];
    if (!plantGroup) return;
    
    const plant = plantsById[selectedPlantGroup];
    const growthDays = plant?.growthDuration || 0;
    const orderIds = plantGroup.orders.map(o => o.id);
    
    // Debug logging to check values
    console.log('Bulk Seeding Assignment Debug:', {
      plantId: selectedPlantGroup,
      plantName: plant?.name,
      growthDuration: plant?.growthDuration,
      seedingDate: seedingDateStr,
      calculatedGrowthDays: growthDays,
      orderCount: orderIds.length,
      orderIds: orderIds
    });
    
    if (growthDays === 0) {
      alert('Warning: Plant growth duration is not set. Delivery date will be same as seeding date.');
    }
    
    await orderService.setBulkSeedingDate(orderIds, new Date(seedingDateStr), growthDays);
    await refreshData();
    setShowBulkModal(false);
    setSelectedPlantGroup(null);
    setSeedingDateStr('');
  };

  const refreshData = async () => {
    const [pendingOrders, grouped] = await Promise.all([
      orderService.getSeedingPendingOrders(),
      orderService.getOrdersGroupedByPlant()
    ]);

    // Enrich orders with plant details for both legacy and multi-plant orders
    const enriched = pendingOrders.map(o => {
      const items = getOrderItems(o);
      const enrichedItems = items.map(item => ({
        ...item,
        plant: item.plantId ? plantsById[item.plantId] : undefined
      }));

      const maxGrowthDuration = Math.max(
        ...enrichedItems.map(item => item.plant?.growthDuration || 0),
        0
      );

      const plant = o.plantId ? plantsById[o.plantId] : undefined;
      return { ...o, plant, enrichedItems, maxGrowthDuration };
    });

    setOrders(enriched);
    setGroupedOrders(grouped);
  };

  const openBulkSeeding = (plantId: string) => {
    setSelectedPlantGroup(plantId);
    setSeedingDateStr(new Date().toISOString().split('T')[0]);
    setShowBulkModal(true);
  };

  const formatDate = (ts: any): string => {
    if (!ts) return '-';
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-IN');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-200">
      <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-8 text-white shadow-xl">
        <h1 className="text-3xl font-bold mb-2">Seeding Planning</h1>
        <p className="text-amber-100 text-lg">Assign seeding dates for new orders</p>
      </div>
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex rounded-md shadow-sm" role="group">
          <button
            type="button"
            onClick={() => setViewMode('orders')}
            className={`px-4 py-2 text-sm font-medium border rounded-l-lg ${
              viewMode === 'orders'
                ? 'bg-green-600 text-white border-green-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Users className="h-4 w-4 mr-2 inline" />
            View by Orders
          </button>
          <button
            type="button"
            onClick={() => setViewMode('plants')}
            className={`px-4 py-2 text-sm font-medium border-t border-b border-r rounded-r-lg ${
              viewMode === 'plants'
                ? 'bg-green-600 text-white border-green-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Package2 className="h-4 w-4 mr-2 inline" />
            View by Plant Type
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search by order, farmer, or plant..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-800 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-colors duration-200"
        />
      </div>

      {/* Orders View */}
      {viewMode === 'orders' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {filtered.map((order) => {
              const items = order.enrichedItems || [];
              const isMultiPlant = items.length > 1;
              const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

              return (
                <li key={order.id}>
                  <div
                    className={`px-4 py-4 sm:px-6 ${isMultiPlant ? 'cursor-pointer hover:bg-purple-50 transition-colors' : ''}`}
                    onClick={isMultiPlant ? () => { setOrderForDetails(order); setShowOrderDetails(true); } : undefined}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isMultiPlant ? 'bg-purple-100' : 'bg-amber-100'}`}>
                            {isMultiPlant ? (
                              <Package2 className="h-5 w-5 text-purple-600" />
                            ) : (
                              <Sprout className="h-5 w-5 text-amber-600" />
                            )}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">#{order.orderId || order.id}</div>
                          <div className="text-sm text-gray-500">{order.customerName}</div>
                          {isMultiPlant && (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                {items.length} plant types
                              </span>
                              <span className="text-xs text-purple-600 flex items-center">
                                <Eye className="h-3 w-3 mr-1" />
                                Click for details
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex-1" onClick={(e) => isMultiPlant && e.stopPropagation()}>
                        {/* Plant details - different display for single vs multi-plant */}
                        {isMultiPlant ? (
                          <div className="bg-gray-50 rounded-lg p-3 mb-2">
                            <p className="text-xs font-medium text-gray-500 mb-2">Order Items (click card for full details):</p>
                            <div className="space-y-1">
                              {items.slice(0, 2).map((item, idx) => (
                                <div key={item.id || idx} className="flex justify-between items-center text-sm">
                                  <div>
                                    <span className="font-medium text-gray-900">{item.plantName}</span>
                                    {item.plantBrand && (
                                      <span className="text-gray-500 ml-1">({item.plantBrand})</span>
                                    )}
                                  </div>
                                  <div className="text-right text-gray-600">
                                    {item.traysRequired} trays
                                  </div>
                                </div>
                              ))}
                              {items.length > 2 && (
                                <div className="text-xs text-purple-600 font-medium pt-1">
                                  + {items.length - 2} more plant{items.length - 2 > 1 ? 's' : ''}...
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-4 text-sm sm:flex sm:items-center sm:gap-6">
                            <div>
                              <p className="text-gray-500">Plant</p>
                              <p className="font-medium text-gray-900">{items[0]?.plantName || order.plant?.name || '-'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Quantity</p>
                              <p className="font-medium text-gray-900">{items[0]?.quantity?.toLocaleString() || order.quantity || '-'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Trays</p>
                              <p className="font-medium text-gray-900">{order.totalTrays}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Growth</p>
                              <p className="font-medium text-gray-900">{order.plant?.growthDuration || items[0]?.plant?.growthDuration || '-'} days</p>
                            </div>
                          </div>
                        )}
                        {/* Summary for multi-plant orders */}
                        {isMultiPlant && (
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">Total Plants</p>
                              <p className="font-medium text-gray-900">{totalQuantity.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Total Trays</p>
                              <p className="font-medium text-gray-900">{order.totalTrays}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Max Growth</p>
                              <p className="font-medium text-purple-600">{order.maxGrowthDuration || '-'} days</p>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2" onClick={(e) => e.stopPropagation()}>
                        <div className="text-sm text-right">
                          <p className="text-gray-500">Order Date</p>
                          <p className="font-medium text-gray-900">{formatDate(order.orderDate)}</p>
                        </div>
                        <div className="flex gap-2">
                          {isMultiPlant && (
                            <button
                              onClick={() => { setOrderForDetails(order); setShowOrderDetails(true); }}
                              className="inline-flex items-center px-3 py-2 text-xs sm:text-sm font-medium text-purple-700 bg-purple-100 border border-purple-200 rounded-md hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Details
                            </button>
                          )}
                          <button
                            onClick={() => openSetDate(order)}
                            className="inline-flex items-center px-3 py-2 text-xs sm:text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          >
                            <Calendar className="h-4 w-4 mr-1" />
                            Set Seeding Date
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Plant Groups View */}
      {viewMode === 'plants' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {Object.entries(groupedOrders).map(([plantId, group]) => (
              <li key={plantId}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                          <Package2 className="h-6 w-6 text-green-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-lg font-medium text-gray-900">{group.plant.name}</div>
                        <div className="text-sm text-gray-500">{group.orders.length} orders • {group.totalTrays} total trays</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">Orders:</div>
                        <div className="text-xs text-gray-500">
                          {group.orders.map(o => `#${o.orderId || o.id}`).join(', ')}
                        </div>
                      </div>
                      <button
                        onClick={() => openBulkSeeding(plantId)}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Seed All for This Plant
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Enhanced Seeding Assignment Modal */}
      {showModal && selectedOrder && (() => {
        const items = selectedOrder.enrichedItems || [];
        const isMultiPlant = items.length > 1;
        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        const growthDuration = selectedOrder.maxGrowthDuration || selectedOrder.plant?.growthDuration || 0;

        return (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 py-4">
            <div className="relative mx-auto p-6 w-11/12 max-w-2xl shadow-2xl rounded-2xl bg-white my-8 mb-24">
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isMultiPlant ? 'bg-gradient-to-br from-purple-500 to-indigo-600' : 'bg-gradient-to-br from-green-500 to-emerald-600'}`}>
                    {isMultiPlant ? (
                      <Package2 className="h-6 w-6 text-white" />
                    ) : (
                      <Sprout className="h-6 w-6 text-white" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Assign Seeding Date</h3>
                    <p className="text-gray-600">
                      Set seeding date for order #{selectedOrder.orderId || selectedOrder.id}
                      {isMultiPlant && <span className="ml-2 text-purple-600 font-medium">({items.length} plant types)</span>}
                    </p>
                  </div>
                </div>

                {/* Order & Plant Details Card */}
                <div className={`rounded-xl p-6 border ${isMultiPlant ? 'bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200' : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'}`}>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Package2 className={`h-5 w-5 mr-2 ${isMultiPlant ? 'text-purple-600' : 'text-green-600'}`} />
                    Order & Plant Details
                  </h4>

                  {/* Customer Info */}
                  <div className="mb-4">
                    <span className="text-sm font-medium text-gray-500">Customer</span>
                    <p className="text-lg font-semibold text-gray-900">{selectedOrder.customerName}</p>
                  </div>

                  {/* Plant Items */}
                  {isMultiPlant ? (
                    <div className="bg-white rounded-lg p-4 border border-purple-100 mb-4">
                      <p className="text-sm font-medium text-purple-700 mb-3">Plants in this order:</p>
                      <div className="space-y-3">
                        {items.map((item, idx) => (
                          <div key={item.id || idx} className="flex justify-between items-center p-2 bg-purple-50 rounded-lg">
                            <div>
                              <span className="font-medium text-gray-900">{item.plantName}</span>
                              {item.plantBrand && (
                                <span className="text-gray-500 text-sm ml-1">({item.plantBrand})</span>
                              )}
                            </div>
                            <div className="text-right text-sm">
                              <span className="text-gray-600">{item.quantity.toLocaleString()} plants • {item.traysRequired} trays</span>
                              <span className="block text-purple-600 font-medium">{item.plant?.growthDuration || 0} days growth</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <span className="text-sm font-medium text-gray-500">Plant Variety</span>
                      <p className="text-lg font-semibold text-green-700">{items[0]?.plantName || selectedOrder.plant?.name || 'Unknown Plant'}</p>
                    </div>
                  )}

                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Total Trays</span>
                      <p className="text-lg font-semibold text-blue-600">{selectedOrder.totalTrays}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Total Plants</span>
                      <p className="text-lg font-semibold text-gray-900">{totalQuantity.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">{isMultiPlant ? 'Max Growth' : 'Growth'}</span>
                      <p className={`text-lg font-semibold ${isMultiPlant ? 'text-purple-600' : 'text-purple-600'}`}>
                        {growthDuration} days
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Order Date</span>
                      <p className="text-gray-900">{formatDate(selectedOrder.orderDate)}</p>
                    </div>
                  </div>

                  {isMultiPlant && (
                    <div className="mt-4 p-3 bg-purple-100 rounded-lg border border-purple-200">
                      <p className="text-sm text-purple-800">
                        <strong>Note:</strong> Delivery date will be calculated using the longest growth duration ({growthDuration} days) to ensure all plants are ready.
                      </p>
                    </div>
                  )}
                </div>

                {/* Seeding Date Input */}
                <div className="bg-white border-2 border-green-100 rounded-xl p-6">
                  <label className="block text-lg font-semibold text-gray-900 mb-3">
                    <Calendar className="h-5 w-5 inline mr-2 text-green-600" />
                    Select Seeding Date
                  </label>
                  <input
                    type="date"
                    value={seedingDateStr}
                    onChange={(e) => setSeedingDateStr(e.target.value)}
                    className="block w-full text-lg border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm"
                  />
                  {seedingDateStr && growthDuration > 0 && (
                    <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm text-green-800 font-medium">
                        📅 Expected Delivery Date: {new Date(new Date(seedingDateStr).getTime() + (growthDuration * 24 * 60 * 60 * 1000)).toLocaleDateString('en-IN', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => { setShowModal(false); setSelectedOrder(null); setSeedingDateStr(''); }}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-xl hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveSeeding}
                    disabled={!seedingDateStr}
                    className={`px-6 py-3 text-sm font-medium text-white border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg ${
                      isMultiPlant
                        ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 focus:ring-purple-500'
                        : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:ring-green-500'
                    }`}
                  >
                    Assign Seeding Date
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Enhanced Bulk Seeding Modal */}
      {showBulkModal && selectedPlantGroup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 py-4">
          <div className="relative mx-auto p-6 w-11/12 max-w-3xl shadow-2xl rounded-2xl bg-white my-8">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Package2 className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Bulk Seeding Assignment</h3>
                  <p className="text-gray-600">Set seeding date for multiple orders of the same plant variety</p>
                </div>
              </div>

              {/* Plant Details Card */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Sprout className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xl font-bold text-blue-900 mb-2">
                      {groupedOrders[selectedPlantGroup]?.plant.name}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <span className="text-sm font-medium text-blue-600">Total Orders</span>
                        <p className="text-2xl font-bold text-blue-900">
                          {groupedOrders[selectedPlantGroup]?.orders.length}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-blue-600">Total Trays</span>
                        <p className="text-2xl font-bold text-blue-900">
                          {groupedOrders[selectedPlantGroup]?.totalTrays}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-blue-600">Growth Duration</span>
                        <p className="text-2xl font-bold text-blue-900">
                          {plantsById[selectedPlantGroup]?.growthDuration || 0} days
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Orders List */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Users className="h-5 w-5 mr-2 text-indigo-600" />
                  Orders to be Seeded
                </h4>
                <div className="max-h-40 overflow-y-auto">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {groupedOrders[selectedPlantGroup]?.orders.map((order, index) => (
                      <div key={order.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-semibold text-xs">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                              #{order.orderId || order.id}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{order.customerName}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Seeding Date Input */}
              <div className="bg-white border-2 border-blue-100 rounded-xl p-6">
                <label className="block text-lg font-semibold text-gray-900 mb-3">
                  <Calendar className="h-5 w-5 inline mr-2 text-blue-600" />
                  Select Seeding Date for All Orders
                </label>
                <input
                  type="date"
                  value={seedingDateStr}
                  onChange={(e) => setSeedingDateStr(e.target.value)}
                  className="block w-full text-lg border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                />
                {seedingDateStr && plantsById[selectedPlantGroup]?.growthDuration && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800 font-medium">
                      📅 Expected Delivery Date for All Orders: {new Date(new Date(seedingDateStr).getTime() + (plantsById[selectedPlantGroup].growthDuration * 24 * 60 * 60 * 1000)).toLocaleDateString('en-IN', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => {
                    setShowBulkModal(false);
                    setSelectedPlantGroup(null);
                    setSeedingDateStr('');
                  }}
                  className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-xl hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkSeeding}
                  disabled={!seedingDateStr}
                  className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 border border-transparent rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
                >
                  Assign Seeding Date to All ({groupedOrders[selectedPlantGroup]?.orders.length} orders)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Details Modal for Multi-Plant Orders */}
      {showOrderDetails && orderForDetails && (() => {
        const items = orderForDetails.enrichedItems || [];
        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        const totalAmount = items.reduce((sum, item) => sum + item.totalPrice, 0);

        return (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 py-4">
            <div className="relative mx-auto p-6 w-11/12 max-w-3xl shadow-2xl rounded-2xl bg-white my-8 mb-24">
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
                      <Package2 className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Order Details</h3>
                      <p className="text-gray-600">Order #{orderForDetails.orderId || orderForDetails.id}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowOrderDetails(false); setOrderForDetails(null); }}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="h-6 w-6 text-gray-500" />
                  </button>
                </div>

                {/* Customer Info */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 border border-gray-200">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Users className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Customer</p>
                      <p className="text-xl font-bold text-gray-900">{orderForDetails.customerName}</p>
                    </div>
                    <div className="ml-auto text-right">
                      <p className="text-sm text-gray-500">Order Date</p>
                      <p className="font-medium text-gray-900">{formatDate(orderForDetails.orderDate)}</p>
                    </div>
                  </div>
                </div>

                {/* Plant Items Table */}
                <div className="bg-white border border-purple-200 rounded-xl overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-5 py-3">
                    <h4 className="text-lg font-semibold text-white flex items-center">
                      <Sprout className="h-5 w-5 mr-2" />
                      Plants in Order ({items.length} types)
                    </h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Plant Name</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Brand</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Variety</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Quantity</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Trays</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Price/Plant</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Growth</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Total</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {items.map((item, idx) => (
                          <tr key={item.id || idx} className="hover:bg-purple-50 transition-colors">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold">
                                {idx + 1}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="font-medium text-gray-900">{item.plantName}</span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {item.plantBrand || '-'}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                {item.plantVariety || '-'}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900">
                              {item.quantity.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900">
                              {item.traysRequired}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-600">
                              ₹{item.pricePerPlant.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                {item.plant?.growthDuration || '-'} days
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                              ₹{item.totalPrice.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-purple-50">
                        <tr>
                          <td colSpan={4} className="px-4 py-3 text-sm font-semibold text-gray-900">
                            Total
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                            {totalQuantity.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                            {orderForDetails.totalTrays}
                          </td>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3 text-right">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-purple-200 text-purple-900">
                              Max: {orderForDetails.maxGrowthDuration} days
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-bold text-purple-700">
                            ₹{totalAmount.toLocaleString()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <p className="text-sm text-blue-600 font-medium">Total Plants</p>
                    <p className="text-2xl font-bold text-blue-900">{totalQuantity.toLocaleString()}</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <p className="text-sm text-green-600 font-medium">Total Trays</p>
                    <p className="text-2xl font-bold text-green-900">{orderForDetails.totalTrays}</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                    <p className="text-sm text-purple-600 font-medium">Max Growth Period</p>
                    <p className="text-2xl font-bold text-purple-900">{orderForDetails.maxGrowthDuration} days</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                    <div className="flex items-center gap-1">
                      <IndianRupee className="h-4 w-4 text-amber-600" />
                      <p className="text-sm text-amber-600 font-medium">Order Value</p>
                    </div>
                    <p className="text-2xl font-bold text-amber-900">₹{totalAmount.toLocaleString()}</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => { setShowOrderDetails(false); setOrderForDetails(null); }}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-xl hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowOrderDetails(false);
                      openSetDate(orderForDetails);
                    }}
                    className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-emerald-600 border border-transparent rounded-xl hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all shadow-lg"
                  >
                    <Calendar className="h-4 w-4 inline mr-2" />
                    Set Seeding Date
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default SeedingPlanning;
